import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import {
  FaUser,
  FaWhatsapp,
  FaPhone,
  FaPassport,
  FaIdCard,
  FaInfoCircle,
} from "react-icons/fa";
import CountryPicker from "./CountryPicker";
import {
  getPatientData,
  clearPatient,
} from "../../api/slices/SearchPatientSlice";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../api/store";
import { ProfileData } from "../../types/patient/SearchPatientModal";
import { PatientData } from "./ConfirmationPopup";
import ErrorNotification from "./Error";

interface Errors {
  name?: string;
  gender?: string;
  whatsapp?: string;
  contact?: string;
  nic?: string;
  passport?: string;
  country?: string;
}
interface OldPatientDetailsProps {
  setFormData: Dispatch<SetStateAction<PatientData>>;

  formData: PatientData;

  onClose: () => void;

  onSubmit: () => void;
}
const OldPatientDetails: React.FC<OldPatientDetailsProps> = ({
  formData,
  setFormData,
  onClose,
  onSubmit,
}) => {
  const genders = [
    { id: 1, value: "male", label: "Male" },
    { id: 2, value: "female", label: "Female" },
  ];

  const initialFormData: PatientData = {
    id: 0,
    name: "",
    gender: "",
    whatsapp: "",
    contact: "",
    nic: "",
    passport: "",
    country: "Sri Lanka",
  };

  const [errors, setErrors] = useState<Errors>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();

  // Get patient data from Redux store
  const {
    patientLoading: isSearching,
    patientData: searchResults,
    PatientError: searchError,
  } = useSelector((state: RootState) => state.searchPatientData);

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setSearchQuery("");
    dispatch(clearPatient());
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.whatsapp) newErrors.whatsapp = "WhatsApp is required";
    if (!formData.contact) newErrors.contact = "Contact is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (formData.country === "Sri Lanka" && !formData.nic)
      newErrors.nic = "NIC is required";
    if (formData.country !== "Sri Lanka" && !formData.passport)
      newErrors.passport = "Passport is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit();
    }
  };

  const handleSearch = async () => {
    if (!searchQuery) {
      toast.warning("Search term can't be empty!", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Clear previous results and form
    dispatch(clearPatient());
    setFormData(initialFormData);

    // Dispatch search action
    await dispatch(getPatientData({ patientChy: searchQuery }));
  };

  const handlePatientSelect = (patient: ProfileData) => {
    setFormData({
      ...initialFormData,
      id: patient.id || 0,
      name: patient.name || "",
      gender: patient.gender || "",
      contact: patient.contact_no || "",
      whatsapp: patient.whatsapp_no || "",
      nic: patient.nic || "",
      passport: patient.passport || "",
      country: patient.nic
        ? "Sri Lanka"
        : patient.passport
        ? "Other"
        : "Sri Lanka",
    });
    setSearchQuery("");
    dispatch(clearPatient());
  };

  const [currentError,setCurrentError] = useState<string|null>();

  // Show error toast if search fails
useEffect(() => {
  if (searchError) {
    console.log('Search error:', searchError);
    // Set error state that the ErrorNotification component will use
    setCurrentError(typeof searchError === 'string' ? searchError : 
                   searchError.message || 'An error occurred');
    
    // Auto-clear after 5 seconds
    const timer = setTimeout(() => setCurrentError(null), 5000);
    return () => clearTimeout(timer);
  }
}, [searchError]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-green-50 to-white">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <span className="bg-green-100 p-2 rounded-lg mr-2">
                <FaUser className="text-green-600" />
              </span>
              Patient Details
            </h2>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <IoMdClose size={20} />
            </button>
          </div>

          <div className="mb-4 md:mb-6 bg-gradient-to-r from-green-50 to-blue-50 p-3 md:p-4 rounded-lg border border-gray-200 mx-4 mt-4">
            <details className="group">
              <summary className="flex items-center cursor-pointer list-none">
                <FaInfoCircle className="text-blue-500 text-lg mt-0.5 mr-3 flex-shrink-0" />
                <h3 className="font-medium text-gray-800">
                  How to register a patient
                </h3>
                <span className="ml-auto transition-transform group-open:rotate-180">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </summary>
              <ul className="mt-3 text-sm text-gray-600 space-y-2 pl-7">
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2"></span>
                  <span>
                    Search first to check if the patient already exists in the
                    system
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2"></span>
                  <span>For Sri Lankan patients, NIC number is mandatory</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2"></span>
                  <span>For foreign patients, passport number is required</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 mr-2"></span>
                  <span>Ensure contact numbers are valid and reachable</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2"></span>
                  <span>Fields marked with * are mandatory</span>
                </li>
              </ul>
            </details>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Search Section */}
            <div className="bg-white p-4 rounded-lg border border-gray-100">
              <h3 className="text-lg font-semibold mb-3 flex items-center text-green-600">
                <IoMdSearch className="mr-2 text-green-400" />
                Search Existing Patient
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Search by NIC, Passport or Contact
                  </label>
                  <div className="relative">
                    <div className="flex">
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-200 rounded-l-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter NIC, Passport or Contact number"
                      />
                      <button
                        onClick={handleSearch}
                        className="px-3 bg-green-500 text-white rounded-r-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                        disabled={isSearching}
                      >
                        {isSearching ? (
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          <IoMdSearch size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-red-500">
                    <strong>Note:</strong> If you've visited us before, please
                    use your CHY number, registered NIC, or mobile number to
                    retrieve your records.
                  </p>
                </div>

                {searchResults && searchResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 border-b border-blue-100 px-4 py-2">
                      <div className="flex items-center">
                        <svg
                          className="h-4 w-4 text-blue-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">Important:</span> Please
                          select your record from the list below, then scroll
                          down to confirm your details.
                        </p>
                      </div>
                    </div>
                    {/* Desktop Table View (hidden on mobile) */}
                    <div className="hidden md:block max-h-48 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Name
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              ID
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Contact
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {searchResults.map((patient) => (
                            <tr key={patient.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                {patient.name}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {patient.nic || patient.passport || "N/A"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {patient.contact_no || "N/A"}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() => handlePatientSelect(patient)}
                                  className="text-green-600 hover:text-green-800 font-medium"
                                >
                                  Select
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card View (shown on mobile) */}
                    <div className="md:hidden max-h-48 overflow-y-auto">
                      <div className="divide-y divide-gray-200">
                        {searchResults.map((patient) => (
                          <div
                            key={patient.id}
                            className="p-3 hover:bg-gray-50"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-sm font-medium text-gray-900">
                                  {patient.name}
                                </h3>
                                <div className="mt-1 text-xs text-gray-500">
                                  <div className="flex items-center">
                                    <span className="font-medium mr-1">
                                      ID:
                                    </span>
                                    <span>
                                      {patient.nic || patient.passport || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex items-center mt-1">
                                    <span className="font-medium mr-1">
                                      Contact:
                                    </span>
                                    <span>{patient.contact_no || "N/A"}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handlePatientSelect(patient)}
                                className="px-3 py-1 bg-green-50 text-green-600 rounded-md text-sm font-medium hover:bg-green-100"
                              >
                                Select
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Form Sections */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Patient Info */}
              <div className="bg-white p-4 rounded-lg border border-gray-100">
                <h3 className="text-lg font-semibold mb-3 flex items-center text-green-700">
                  <FaUser className="mr-2 text-green-500" />
                  Patient Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {genders.map((gender) => (
                        <label
                          key={gender.id}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                            formData.gender === gender.value
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="gender"
                            value={gender.value}
                            checked={formData.gender === gender.value}
                            onChange={() =>
                              setFormData({ ...formData, gender: gender.value })
                            }
                            className="h-4 w-4 text-green-600 focus:ring-green-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {gender.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.gender && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.gender}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1 flex items-center">
                      <FaWhatsapp className="mr-1 text-green-500" />
                      WhatsApp <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                      value={formData.whatsapp}
                      onChange={(e) =>
                        setFormData({ ...formData, whatsapp: e.target.value })
                      }
                      placeholder="+94 76 123 4567"
                    />
                    {errors.whatsapp && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.whatsapp}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1 flex items-center">
                      <FaPhone className="mr-1 text-green-500" />
                      Contact Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                      value={formData.contact}
                      onChange={(e) =>
                        setFormData({ ...formData, contact: e.target.value })
                      }
                      placeholder="+94 11 123 4567"
                    />
                    {errors.contact && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.contact}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <CountryPicker
                      onChange={(e: any) =>
                        setFormData({ ...formData, country: e.countryData })
                      }
                      country={formData.country}
                    />
                    {errors.country && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.country}
                      </p>
                    )}
                  </div>

                  {formData.country === "Sri Lanka" ? (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1 flex items-center">
                        <FaIdCard className="mr-1 text-purple-500" />
                        NIC <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                        value={formData.nic}
                        onChange={(e) =>
                          setFormData({ ...formData, nic: e.target.value })
                        }
                        placeholder="123456789V"
                      />
                      {errors.nic && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.nic}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1 flex items-center">
                        <FaPassport className="mr-1 text-purple-500" />
                        Passport <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-500"
                        value={formData.passport}
                        onChange={(e) =>
                          setFormData({ ...formData, passport: e.target.value })
                        }
                        placeholder="A12345678"
                      />
                      {errors.passport && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.passport}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-sm"
                >
                  Save Patient
                </button>
              </div>
            </form>
          </div>
        </motion.div>
        {
            currentError && <ErrorNotification error={currentError}/>
        }
      </motion.div>
    </AnimatePresence>
  );
};
// currentError
export default OldPatientDetails;
